import { PureComponent } from 'react';
import { noop, some, pick, isFunction, isNil } from 'lodash';
import { FastField, getIn } from 'formik';

function composeValidators(validators) {
  if (validators.length) {
    return (value) => {
      for (const fn of validators) {
        const err = fn(value);
        if (err) {
          return err;
        }
      }

      return undefined;
    };
  }

  return undefined;
}

// Use `passProps` to specify any Formik Form specific props that should be
// passed down to child components.
// Use `watchedProps` to specify any props that should be watched for changes
// for rendering purposes. Formik FastField ignores all non-Formik props by
// default. Note that all `passProps` are already watched.
export default function withFormikInput(Component, { passProps = [], watchedProps = [], validators = [] } = {}) {
  return class FormikFieldWrapper extends PureComponent {
    static defaultProps = {
      injectedFields: undefined,
      onFieldBlur: noop,
      onFieldChange: noop,
      validators: [],
    };

    hasError = (field, form) => Boolean(getIn(form.touched, field.name) && getIn(form.errors, field.name));

    getOnChange = (field) => (eventOrPath) => {
      field.onChange(eventOrPath);
      // @ts-expect-error TS(2339): Property 'onFieldChange' does not exist on type 'R... Remove this comment to see the full error message
      this.props.onFieldChange(eventOrPath);
    };

    getOnBlur = (field) => (eventOrPath) => {
      field.onBlur(eventOrPath);
      // @ts-expect-error TS(2339): Property 'onFieldBlur' does not exist on type 'Rea... Remove this comment to see the full error message
      this.props.onFieldBlur(eventOrPath);
    };

    shouldUpdate = (currentProps, prevProps) => {
      const watchedPropsChanged = some(
        watchedProps,
        (propName) => getIn(currentProps, propName) !== getIn(prevProps, propName)
      );

      const passPropsChanged = some(
        passProps,
        (propName) => getIn(currentProps, propName) !== getIn(prevProps, propName)
      );

      // https://github.com/jaredpalmer/formik/issues/1238
      // the reason of `customShouldUpdate` is that right now if there is a
      // `shouldUpdate` defined on the FastField component it will not fallback
      // to the default behaviour of `shouldComponentUpdate `
      // even if the `shouldUpdate` returns `false`
      // if I add into my component `shouldUpdate` then I would need to copy/paste
      // again the bits that we copied from the Formik FastField `shouldComponentUpdate`
      const customShouldUpdate = isFunction(currentProps.customShouldUpdate)
        ? currentProps.customShouldUpdate(prevProps, currentProps)
        : false;

      // track changes on injected fields as some fields depend
      // on the state of other fields
      const injectedFieldsUpdate = some(
        currentProps.injectedFields,
        (injectedField) =>
          getIn(currentProps.formik.values, injectedField.name) !== getIn(prevProps.formik.values, injectedField.name)
      );

      const disabledAttributeChanged = getIn(currentProps, 'disabled') !== getIn(prevProps, 'disabled');

      // Mostly copied from the Formik FastField `shouldComponentUpdate`
      if (
        watchedPropsChanged ||
        passPropsChanged ||
        customShouldUpdate ||
        injectedFieldsUpdate ||
        disabledAttributeChanged ||
        getIn(currentProps.formik.values, currentProps.name) !== getIn(prevProps.formik.values, currentProps.name) ||
        getIn(currentProps.formik.errors, currentProps.name) !== getIn(prevProps.formik.errors, currentProps.name) ||
        getIn(currentProps.formik.touched, currentProps.name) !== getIn(prevProps.formik.touched, currentProps.name) ||
        Object.keys(currentProps).length !== Object.keys(prevProps).length ||
        currentProps.formik.isSubmitting !== prevProps.formik.isSubmitting
      ) {
        return true;
      } else {
        return false;
      }
    };

    renderContent = (formikBag) => {
      const { field, form } = formikBag;
      const hasError = this.hasError(field, form);
      const formProps = pick(form, passProps);
      const value = getIn(form.values, field.name);
      // @ts-expect-error TS(2339): Property 'customShouldUpdate' does not exist on ty... Remove this comment to see the full error message
      const { customShouldUpdate, injectedFields, onFieldChange, onFieldBlur, ...rest } = this.props;

      if (passProps.includes('touched')) {
        formProps.touched = getIn(form.touched, field.name);
      } else {
        delete formProps.touched;
      }

      // do not polute the DOM with the validator code
      // @ts-expect-error TS(2339): Property 'validators' does not exist on type '{ ch... Remove this comment to see the full error message
      delete rest.validators;

      // Some fields depend on the state of other fields
      if (injectedFields) {
        injectedFields.forEach((injectedField) => {
          formProps[injectedField.nameAs || injectedField.name] = getIn(form.values, injectedField.name);
        });
      }

      if (typeof value === 'object' && !isNil(value)) {
        formProps.subFields = Object.keys(value);
      }

      return (
        <Component
          {...field}
          onChange={this.getOnChange(field)}
          onBlur={this.getOnBlur(field)}
          error={hasError}
          {...formProps}
          {...rest}
        />
      );
    };

    render() {
      // @ts-expect-error TS(2339): Property 'validators' does not exist on type 'Read... Remove this comment to see the full error message
      const { validators: _validators, ...rest } = this.props;

      const validationFn = composeValidators([...validators, ..._validators]);

      return (
        <FastField validate={validationFn} shouldUpdate={this.shouldUpdate} {...rest}>
          {this.renderContent}
        </FastField>
      );
    }
  };
}
